import { AppModule } from 'app/module';
import { Input } from 'module/input/main';
import { Dropdown } from 'module/dropdown/main';
import { Autocomplete } from 'module/autocomplete/main';
import { getJson } from 'util/xhr';
import { smoothScrollToElement } from 'util/scroll';
import 'module/textarea/main';
import 'module/radioButton/main';
import { API_ENDPOINT_AUTOCOMPLETE_JOBS } from 'app/endpoints';
import Template from './template.hbs';
import './styles.scss';

export class SalaryBenchmarkForm extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        /* eslint-disable max-len */
        this.dom.form = this.dom.el.querySelector('.m-salaryBenchmarkForm__form');
        this.dom.submitButton = this.dom.el.querySelector('.m-salaryBenchmarkForm__mainButton');
        this.dom.jobsAutocomplete = this.dom.el.querySelector('.m-salaryBenchmarkForm__jobsAutocomplete .m-autocomplete');
        this.dom.jobid = this.dom.el.querySelector('.m-salaryBenchmarkForm__jobid');

        // Inputs
        this.dom.workHours = this.dom.el.querySelector('.m-salaryBenchmarkForm__workHours .m-input');
        this.dom.jobs = this.dom.el.querySelector('.m-salaryBenchmarkForm__jobs .m-input');
        this.dom.grossSalary = this.dom.el.querySelector('.m-salaryBenchmarkForm__grossSalary .m-input');

        this.workHours = new Input(this.dom.workHours);
        this.grossSalary = new Input(this.dom.grossSalary);
        this.jobs = new Input(this.dom.jobs);
        this.jobsAutocomplete = new Autocomplete(this.dom.jobsAutocomplete);
        this.jobsAutocomplete.set({
            input: this.jobs,
            endpoint: API_ENDPOINT_AUTOCOMPLETE_JOBS,
        });

        // Dropdowns
        this.dom.federalState = this.dom.el.querySelector('.m-salaryBenchmarkForm__federalState .m-dropdown');
        this.dom.graduation = this.dom.el.querySelector('.m-salaryBenchmarkForm__graduation .m-dropdown');
        this.dom.age = this.dom.el.querySelector('.m-salaryBenchmarkForm__age .m-dropdown');
        this.dom.sector = this.dom.el.querySelector('.m-salaryBenchmarkForm__sector .m-dropdown');
        this.dom.experience = this.dom.el.querySelector('.m-salaryBenchmarkForm__experience .m-dropdown');
        this.dom.subSector = this.dom.el.querySelector('.m-salaryBenchmarkForm__subSector .m-dropdown');
        this.dom.employees = this.dom.el.querySelector('.m-salaryBenchmarkForm__employees .m-dropdown');

        this.federalState = new Dropdown(this.dom.federalState);
        this.graduation = new Dropdown(this.dom.graduation);
        this.age = new Dropdown(this.dom.age);
        this.sector = new Dropdown(this.dom.sector);
        this.subSector = new Dropdown(this.dom.subSector);
        this.experience = new Dropdown(this.dom.experience);
        this.employees = new Dropdown(this.dom.employees);
        /* eslint-enable max-len */
    }

    domEvents() {
        this.dom.form.addEventListener('submit', (e) => this.submit(e));
    }

    subEvents() {
        this.sector.events.on('change', (e) => this.loadSubSectors(e));
        this.jobs.events.on('click', () => {
            this.jobs.setValid();
            this.jobs.setFocus();
        });

        this.jobs.events.on('select', () => {
            this.jobs.setValid();
            this.jobs.setFocus();
        });

        this.jobsAutocomplete.events.on('setId', (e) => {
            this.dom.jobid.value = e.arguments[0]; // eslint-disable-line prefer-destructuring
            if (e.arguments[1]) {
                this.submit();
            }
        });

        this.jobsAutocomplete.events.on('clearJobId', () => {
            this.dom.jobid.value = '';
        });

        this.jobsAutocomplete.events.on('autoCompleteItemClicked', (e) => {
            this.dom.jobid.value = e.arguments[1]; // eslint-disable-line prefer-destructuring
        });

        this.federalState.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
        this.graduation.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
        this.age.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
        this.sector.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
        this.subSector.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
        this.experience.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
        this.employees.events.on('closeAllDropdowns', () => this.closeAllDropdowns());
    }

    closeAllDropdowns() {
        this.age.closeDropdown();
        this.federalState.closeDropdown();
        this.graduation.closeDropdown();
        this.sector.closeDropdown();
        this.subSector.closeDropdown();
        this.experience.closeDropdown();
        this.employees.closeDropdown();
    }

    ready() {
        this.showSubSectorsDropdown();
    }

    submit(e) {
        if (e) e.preventDefault();
        const hasActiveAutocomplete = this.hasActiveAutocomplete();
        this.jobsAutocomplete.insertSelectedItem();

        if (!hasActiveAutocomplete) {
            if (this.validate()) {
                this.dom.submitButton.disabled = true;
                this.dom.form.submit();
            } else {
                this.scrollToFirstError();
            }
        }
    }

    validationRules() {
        this.validation.addRule(this.grossSalary, {
            rules: [{
                name: 'rangeFloat@gross_salary:500|15000',
                message: ([min, max]) => `Dein Gehalt muss zwischen €\u00a0${min} und €\u00a0${max} liegen.`,
            }, {
                name: 'numeric',
                message: 'Bitte gib dein Gehalt mit maximal zwei Nachkommastellen ein.',
            }],
        });
        this.validation.addRule(this.workHours, {
            rules: [{
                name: 'rangeWorkHours@work_hours:15|60',
                message: ([min, max]) => `Die Arbeitszeit muss zwischen ${min} und ${max}
                Wochenstunden liegen (auf 0,5 Stunden genau).`,
            }],
        });
        this.validation.addRule(this.federalState, {
            rules: [{
                name: 'required',
                message: 'Bitte wähle ein Bundesland aus.',
            }],
        });
        this.validation.addRule(this.age, {
            rules: [{
                name: 'required',
                message: 'Bitte gib dein Alter an.',
            }],
        });
        this.validation.addRule(this.sector, {
            rules: [{
                name: 'required',
                message: 'Bitte wähle eine Branche aus.',
            }],
        });
        this.validation.addRule(this.experience, {
            rules: [{
                name: 'maxExperience',
                test: (yearsInCompany) => this.age.getValue() - 14 >= yearsInCompany,
                message: 'Die Firmenzugehörigkeit muss mindestens 14 Jahre unter der Altersangabe liegen.',
            }],
        });
        this.validation.addRule(this.employees, {
            rules: [{
                name: 'required',
                message: 'Bitte wähle die Anzahl der Mitarbeiter*innen aus.',
            }],
        });
        this.validation.addRule(this.graduation, {
            rules: [{
                name: 'required',
                message: 'Bitte gib deinen höchsten Abschluss an.',
            }],
        });
        this.validation.addRule(this.jobs, {
            rules: [{
                name: 'required',
                message: 'Bitte gib deinen Beruf ein.',
            }, {
                name: 'no_benchmark_job',
                test: (value) => {
                    const jobId = this.dom.jobid.value;
                    return value !== '' && jobId !== '';
                },
                message: 'Für diesen Job können wir noch keine Daten liefern.',
            }],
            sequential: true,
        });
    }

    showSubSectorsDropdown() {
        const sectorId = this.sector.getValue();

        if (sectorId && sectorId !== '' && sectorId > 0) {
            document.querySelector('.m-salaryBenchmarkForm__subSector')
                .classList.add('m-salaryBenchmarkForm__subSector--shown');
        }
    }

    loadSubSectors() {
        const sectorId = this.sector.getValue();
        getJson(
            `/gehalt/branch/${sectorId}`,
            {},
            (response) => this.onResponse(response),
        );

        return true;
    }

    onResponse(response) {
        let subSectorObj = {};
        let subSectorName;

        const options = [];
        Object.keys(response).forEach((key) => {
            if (key && response[key]) {
                subSectorName = response[key];
                subSectorObj = { name: subSectorName, value: key };
                options.push(subSectorObj);
            }
        });

        const text = options[0].name;
        const currentValue = options[0].value;

        this.subSector.updateProps({ options, text, currentValue }, true);

        document.querySelector('.m-salaryBenchmarkForm__subSector')
            .classList.add('m-salaryBenchmarkForm__subSector--shown');
    }

    scrollToFirstError() {
        const $firstError = this.dom.el.querySelector('[class*="error"]');

        smoothScrollToElement($firstError);
    }

    hasActiveAutocomplete() {
        const jobsAutocompleteProps = this.jobsAutocomplete.getProps();

        if (jobsAutocompleteProps && jobsAutocompleteProps.selectedIndex) {
            return true;
        }
        return false;
    }
}
